import { type Restaurant, type RestaurantSignal } from '@prisma/client'

// ⚖️ AUTHORITY WEIGHTS - How much we trust each source type
const AUTHORITY_WEIGHTS = {
	NAMED_CRITIC: 5, // Jay Rayner, Grace Dent, Marina O'Loughlin
	STAFF_CRITIC: 3, // Guardian staff, Standard staff
	EDITORIAL: 2, // Time Out, Hot Dinners, Eater
	BLOGGER: 1, // Independent bloggers
} as const

// 📄 DEPTH MULTIPLIERS - How substantial is the coverage?
const DEPTH_MULTIPLIERS = {
	DEDICATED: 1.5, // Full review article
	LISTICLE: 1.0, // Listed in a guide with a paragraph
	MENTION: 0.5, // Brief mention
	LISTING: 0, // Info/directory page only - no review content, should not count
} as const

// 📊 SCORING CONFIG - Quality-focused: what critics recommend matters most
const SCORING = {
	criticMax: 70, // Max points from critic recommendations (primary signal)
	consensusBonus: 10, // Bonus for agreement (nice to have, not essential)
	volumeBonus: 5, // Small bonus for coverage (quality > quantity)
	googleMax: 15, // Public opinion as supporting signal
	recencyThreshold: 2023, // Reviews from this year+ get boost
	recencyBoost: 1.2, // 20% boost for recent reviews
} as const

export function calculateConsensus(
	restaurant: Restaurant & { signals: RestaurantSignal[] },
) {
	// Filter out LISTING signals - info/directory pages shouldn't count as reviews
	const signals = restaurant.signals.filter((s) => s.reviewDepth !== 'LISTING')

	if (signals.length === 0) {
		return {
			total: 0,
			confidence: 0,
			riskNote: null,
			breakdown: {
				critic: 0,
				consensus: 0,
				volume: 0,
				google: 0,
				signalCount: 0,
				topCritic: null,
			},
		}
	}

	// --- 1. CALCULATE WEIGHTED RECOMMENDATION ---
	let weightedSum = 0
	let totalWeight = 0
	const recommendations: number[] = []

	for (const signal of signals) {
		// Get authority weight (fall back based on type/tier for backwards compat)
		const authority = signal.authority || inferAuthority(signal)
		const authorityWeight =
			AUTHORITY_WEIGHTS[authority as keyof typeof AUTHORITY_WEIGHTS] || 1

		// Get depth multiplier
		const depth = signal.reviewDepth || 'DEDICATED'
		const depthMultiplier =
			DEPTH_MULTIPLIERS[depth as keyof typeof DEPTH_MULTIPLIERS] || 1

		// Recency boost
		const reviewYear = signal.reviewYear || 2020
		const recencyMultiplier =
			reviewYear >= SCORING.recencyThreshold ? SCORING.recencyBoost : 1.0

		// Use recommendation if available, otherwise fall back to sentiment
		const rec = signal.recommendation ?? signal.sentiment ?? 0.5
		recommendations.push(rec)

		// Calculate final weight for this signal
		const weight = authorityWeight * depthMultiplier * recencyMultiplier
		weightedSum += rec * weight
		totalWeight += weight
	}

	const weightedAvg = totalWeight > 0 ? weightedSum / totalWeight : 0
	const criticScore = weightedAvg * SCORING.criticMax

	// --- 2. CONSENSUS BONUS (Agreement) ---
	// High agreement = high confidence = bonus points
	const mean =
		recommendations.reduce((a, b) => a + b, 0) / recommendations.length
	const variance =
		recommendations.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) /
		recommendations.length
	const stdDev = Math.sqrt(variance)

	// Agreement: 1.0 = perfect agreement, 0.0 = wildly different opinions
	// stdDev of 0 = perfect agreement, stdDev of 0.5+ = total chaos
	const agreement = Math.max(0, 1 - stdDev * 2)
	// Require at least 2 signals for consensus - one opinion isn't consensus
	const consensusBonus =
		signals.length >= 2 ? agreement * SCORING.consensusBonus : 0

	// --- 3. VOLUME BONUS (More sources = more confidence) ---
	// Diminishing returns: 1 source = 2pts, 5 sources = 10pts max
	const volumeBonus = Math.min(signals.length, 5) * 2

	// --- 4. DISSENT DETECTION ---
	// Find if any high-authority source disagrees with consensus
	const highAuthoritySignals = signals.filter((s) => {
		const auth = s.authority || inferAuthority(s)
		return auth === 'NAMED_CRITIC' || auth === 'STAFF_CRITIC'
	})

	const dissenter = highAuthoritySignals.find((s) => {
		const rec = s.recommendation ?? s.sentiment ?? 0.5
		return Math.abs(rec - weightedAvg) > 0.25 // Differs by more than 0.25
	})

	let riskNote: string | null = null
	if (dissenter) {
		const rec = dissenter.recommendation ?? dissenter.sentiment ?? 0.5
		const criticName = dissenter.criticName || dissenter.sourceName
		if (rec < weightedAvg) {
			riskNote = `${criticName} had reservations`
		} else {
			riskNote = `${criticName} rated higher than average`
		}
	}

	// --- 5. GOOGLE COMPONENT ---
	const rating = restaurant.googleRating ?? 0
	const reviews = restaurant.totalReviewCount ?? 0
	let googleScore = 0

	if (rating > 0) {
		// Rating: 4.0 = 0pts, 4.5 = 7.5pts, 5.0 = 15pts (max 15)
		const ratingScore = Math.max(
			0,
			Math.min((rating - 4.0) * 15, SCORING.googleMax),
		)
		// Volume bonus: logarithmic, small contribution (max 3pts)
		const volumeScore = reviews > 0 ? Math.min(Math.log10(reviews) * 1.2, 3) : 0
		// Total Google contribution capped at googleMax
		googleScore = Math.min(ratingScore + volumeScore, SCORING.googleMax)
	}

	// --- 6. MANUAL BOOST ---
	const boostPoints = restaurant.manualBoost ?? 0

	// --- 7. HIDDEN GEM BOOST ---
	// High Google rating, low volume, but critic-approved
	// Require at least 2 signals - need real validation, not just 1 blogger
	let hiddenGemBonus = 0
	if (
		rating >= 4.7 &&
		reviews < 150 &&
		weightedAvg >= 0.7 &&
		signals.length >= 2
	) {
		hiddenGemBonus = 5
	}

	// --- 8. FINAL CALCULATION ---
	const rawScore =
		criticScore + consensusBonus + volumeBonus + googleScore + hiddenGemBonus

	// Confidence ceiling: limit max score based on signal count AND quality
	// A single elite critic review is worth more than multiple blog posts
	const confidenceCap = calculateConfidenceCap(signals)
	const cappedScore = Math.min(confidenceCap, Math.max(0, Math.round(rawScore)))

	// Apply manual boost AFTER confidence cap - founder override
	const finalScore = Math.max(0, cappedScore + boostPoints)

	// Find the top critic for display
	const topCritic = findTopCritic(signals)

	return {
		total: finalScore,
		confidence: agreement,
		riskNote,
		breakdown: {
			critic: Math.round(criticScore),
			consensus: Math.round(consensusBonus),
			volume: Math.round(volumeBonus),
			google: Math.round(googleScore),
			boost: boostPoints,
			hiddenGem: hiddenGemBonus,
			signalCount: signals.length,
			weightedAvg: Math.round(weightedAvg * 100) / 100,
			topCritic,
		},
	}
}

// Infer authority from old type/tier fields for backwards compatibility
function inferAuthority(signal: RestaurantSignal): string {
	if (signal.tier === 1 || signal.type === 'ELITE') {
		// Check if it's a known named critic source
		const name = signal.sourceName?.toLowerCase() || ''
		if (
			name.includes('guardian') ||
			name.includes('observer') ||
			name.includes('standard') ||
			name.includes('times')
		) {
			return 'STAFF_CRITIC'
		}
		return 'EDITORIAL' // Default elite to editorial
	}
	if (signal.tier === 2 || signal.type === 'EDITORIAL') {
		return 'EDITORIAL'
	}
	return 'BLOGGER'
}

// Calculate confidence cap based on signal count AND quality
// Top scores (90+) require elite critics, not just blogger volume
function calculateConfidenceCap(signals: RestaurantSignal[]): number {
	if (signals.length === 0) return 0

	// Count signals by authority type
	const namedCriticCount = signals.filter((s) => {
		const auth = s.authority || inferAuthority(s)
		const depth = s.reviewDepth || 'DEDICATED'
		return (
			auth === 'NAMED_CRITIC' && (depth === 'DEDICATED' || depth === 'LISTICLE')
		)
	}).length

	const staffCriticCount = signals.filter((s) => {
		const auth = s.authority || inferAuthority(s)
		const depth = s.reviewDepth || 'DEDICATED'
		return (
			auth === 'STAFF_CRITIC' && (depth === 'DEDICATED' || depth === 'LISTICLE')
		)
	}).length

	const editorialCount = signals.filter((s) => {
		const auth = s.authority || inferAuthority(s)
		const depth = s.reviewDepth || 'DEDICATED'
		return (
			auth === 'EDITORIAL' && (depth === 'DEDICATED' || depth === 'LISTICLE')
		)
	}).length

	// ELITE TIER (90-98): Requires NAMED_CRITIC validation
	// Multiple named critics OR named critic + staff/editorial depth
	if (namedCriticCount >= 2) return 98
	if (namedCriticCount >= 1 && (staffCriticCount >= 2 || editorialCount >= 2))
		return 95
	if (namedCriticCount >= 1 && signals.length >= 3) return 90

	// HIGH TIER (80-89): Strong editorial/staff critic coverage
	if (staffCriticCount >= 2 && editorialCount >= 1) return 85
	if (staffCriticCount >= 1 && editorialCount >= 2) return 82
	if (editorialCount >= 3) return 80

	// SOLID TIER (70-79): Good coverage but no elite critics
	if (staffCriticCount >= 1 && signals.length >= 3) return 78
	if (editorialCount >= 2 && signals.length >= 3) return 76
	if (namedCriticCount >= 1) return 75
	if (signals.length >= 4) return 75 // Volume can reach "Don't Miss" threshold
	if (signals.length >= 3) return 72

	// MODERATE TIER (60-69): Limited coverage
	if (staffCriticCount >= 1 || editorialCount >= 2) return 70
	if (signals.length === 2) return 68

	// LOW TIER: Single signal or bloggers only
	if (editorialCount >= 1) return 60
	return 55 // Bloggers only
}

// Find the highest-authority positive signal for display
function findTopCritic(
	signals: RestaurantSignal[],
): { name: string; score: number } | null {
	const sorted = [...signals].sort((a, b) => {
		const authA =
			AUTHORITY_WEIGHTS[
				(a.authority || inferAuthority(a)) as keyof typeof AUTHORITY_WEIGHTS
			] || 1
		const authB =
			AUTHORITY_WEIGHTS[
				(b.authority || inferAuthority(b)) as keyof typeof AUTHORITY_WEIGHTS
			] || 1
		const recA = a.recommendation ?? a.sentiment ?? 0
		const recB = b.recommendation ?? b.sentiment ?? 0
		// Sort by authority first, then by recommendation
		return authB * recB - authA * recA
	})

	const top = sorted[0]
	if (!top) return null

	return {
		name: top.criticName || top.sourceName,
		score: top.recommendation ?? top.sentiment ?? 0,
	}
}

export function getScoreContext(score: number): {
	label: string
	color: string
} {
	if (score >= 75) return { label: 'Strong Signal', color: '#22c55e' }
	if (score >= 60) return { label: 'Solid Signal', color: '#00e0ff' }
	if (score >= 45) return { label: 'Building Buzz', color: '#a3e635' }
	if (score >= 30) return { label: 'Under the Radar', color: '#ff9f1c' }
	return { label: 'Light Coverage', color: '#b0b0e0' }
}

export function getScoreSupportText(score: number): string {
	if (score >= 75) return 'Strong, consistent praise across multiple sources.'
	if (score >= 60) return 'Well-backed by reviews and repeat mentions.'
	if (score >= 45) return 'Useful signal, but still lighter than the standouts.'
	if (score >= 30) return 'Less written about, but enough signal to surface it.'
	return 'Light coverage. Worth knowing, but not heavily written about.'
}

export function getScoreExplainer(score: number): string {
	const base =
		'Signal score reflects how strong and consistent the review coverage is, not a star rating.'
	if (score < 45) {
		return `${base} Some quieter cafes and neighbourhood spots naturally have a lighter signal.`
	}
	return base
}
